import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { getUserProfileOrdersSegment, getUserProfileSegment } from "../userProfileSidebarMode";
import "./order-sidebar.css";

const ORDER_ICON_SRC = {
  deposit: "/images/ordersidebar_menu_icon7.svg",
  withdraw: "/images/ordersidebar_menu_icon8.svg",
  transfer: "/images/ordersidebar_menu_icon9.svg",
  // referral: "/images/ordersidebar_menu_icon10.svg",
};

/**
 * Orders-only sidebar (spot, futures tree, convert, launchpad, etc.).
 * @param {{ toggleContent?: (label: string) => void }} props
 */
export default function OrderSidebar({ toggleContent }) {
  const location = useLocation();
  const topSeg = getUserProfileSegment(location.pathname);
  const seg = topSeg === "orders" ? getUserProfileOrdersSegment(location.pathname) : topSeg;

  const fire = (label) => () => toggleContent?.(label);

  const liActive = (segment) => (seg === segment ? "active mb-1" : "mb-1");

  const spotOrderSectionActive = seg === "spot_orders" || seg === "spot_order_history" || seg === "spot_position_history" || seg === "spot_open_orders";
  const earningSectionActive = seg === "staking_orders" || seg === "launchpad_orders";
  const futuresSectionActive = (seg && seg.startsWith("futures_")) || seg === "position_orders" || seg === "position_history";
  const optionsSectionActive = seg === "options_open_orders" || seg === "options_order_history" || seg === "options_trade_history" || seg === "options_position_history";
  const marginSectionActive = seg === "margin_orders" || seg === "margin_order_history" || seg === "margin_position_history" || seg === "margin_trade_history" || seg === "margin_transfer_history" || seg === "margin_borrowing_history";
  const otcSectionActive = seg === "otc_trade" || seg === "otc_orders" || seg === "otc_order_history";

  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 992px)");
    const apply = () => setIsDesktop(Boolean(mq.matches));
    apply();
    if (mq.addEventListener) mq.addEventListener("change", apply);
    else mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", apply);
      else mq.removeListener(apply);
    };
  }, []);

  const [openFuture, setOpenFuture] = useState(false);
  const [openSpot, setOpenSpot] = useState(false);
  const [openMargin, setOpenMargin] = useState(false);
  const [openEarning, setOpenEarning] = useState(false);
  const [openOtc, setOpenOtc] = useState(false);

  useEffect(() => {
    // Auto-open Future section when on Futures/Options subpages
    const shouldOpen = Boolean(futuresSectionActive || optionsSectionActive);
    setOpenFuture(shouldOpen);
  }, [futuresSectionActive, optionsSectionActive]);

  useEffect(() => {
    // Desktop only: auto-open Spot dropdown on Spot subpages
    if (!isDesktop) {
      setOpenSpot(false);
      return;
    }
    setOpenSpot(Boolean(spotOrderSectionActive));
  }, [isDesktop, spotOrderSectionActive]);

  useEffect(() => {
    if (!isDesktop) { setOpenMargin(false); return; }
    setOpenMargin(Boolean(marginSectionActive));
  }, [isDesktop, marginSectionActive]);

  useEffect(() => {
    if (!isDesktop) {
      setOpenEarning(false);
      return;
    }
    setOpenEarning(Boolean(earningSectionActive));
  }, [isDesktop, earningSectionActive]);

  useEffect(() => {
    if (!isDesktop) {
      setOpenOtc(false);
      return;
    }
    setOpenOtc(Boolean(otcSectionActive));
  }, [isDesktop, otcSectionActive]);

  return (
    <ul className="list-unstyled ps-0 navi_sidebar order-sidebar-root">
      {isDesktop ? (
        <li className={`mb-1 order-sidebar-deep ${openSpot ? "active" : ""}`}>
          <button
            type="button"
            className={`btn btn-toggle collapsed ${spotOrderSectionActive ? "active" : ""}`}
            onClick={() => setOpenSpot((v) => !v)}
          >
            <img className="navi_sidebar_icon" src="/images/ordersidebar_menu_icon.svg" alt="Spot" width={20} height={20} />
            <div className={`dashboard_menu_hd ${spotOrderSectionActive ? "active_ul" : ""}`}>Spot Order</div>
            <span>
              <i className={openSpot ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true" />
            </span>
          </button>
          <div className={`collapse ${openSpot ? "show" : ""}`}>
            <ul className="btn-toggle-nav list-unstyled fw-normal pb-1 small">
              <li className={liActive("spot_orders")} onClick={fire("Spot Order")}>
                <Link to="/user_profile/orders/spot_orders" className="rounded">
                  Open Orders
                </Link>
              </li>
              <li className={liActive("spot_order_history")} onClick={fire("Spot Order")}>
                <Link to="/user_profile/orders/spot_order_history" className="rounded">
                  Order History
                </Link>
              </li>
              <li className={liActive("spot_position_history")} onClick={fire("Spot Order")}>
                <Link to="/user_profile/orders/spot_position_history" className="rounded">
                  Trade History
                </Link>
              </li>
            </ul>
          </div>
        </li>
      ) : (
        <li className={spotOrderSectionActive ? "active mb-1" : "mb-1"} onClick={fire("Spot Order")}>
          <Link to="/user_profile/orders/spot_orders">
            <img className="navi_sidebar_icon" src="/images/ordersidebar_menu_icon.svg" alt="Spot" width={20} height={20} />
            <div className="dashboard_menu_hd">Spot Order</div>
          </Link>
        </li>
      )}

      {/* Future → (only) Perpetual Futures + OptionsOrder. Deep submenus moved to in-page tabs. */}
      <li className={`mb-1 order-sidebar-deep ${openFuture ? "active" : ""}`}>
        <button
          type="button"
          className={`btn btn-toggle collapsed ${futuresSectionActive || optionsSectionActive ? "active" : ""}`}
          onClick={() => setOpenFuture((v) => !v)}
        >
          <img className="navi_sidebar_icon" src="/images/ordersidebar_menu_icon2.svg" alt="Future" width={20} height={20} />
          <div className={`dashboard_menu_hd ${futuresSectionActive || optionsSectionActive ? "active_ul" : ""}`}>Future</div>
          <span>
            <i className={openFuture ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true" />
          </span>
        </button>
        <div className={`collapse ${openFuture ? "show" : ""}`}>
          <ul className="btn-toggle-nav list-unstyled fw-normal pb-1 small">
            <li className={liActive("futures_open_orders")} onClick={fire("Futures Open Order")}>
              <Link to="/user_profile/orders/futures_open_orders" className="rounded">
                Perpetual Futures Order
              </Link>
            </li>
            <li className={liActive("options_open_orders")} onClick={fire("Options Open Orders")}>
              <Link to="/user_profile/orders/options_open_orders" className="rounded">
                OptionsOrder
              </Link>
            </li>
          </ul>
        </div>
      </li>

      {isDesktop ? (
        <li className={`mb-1 order-sidebar-deep ${openMargin ? "active" : ""}`}>
          <button
            type="button"
            className={`btn btn-toggle collapsed ${marginSectionActive ? "active" : ""}`}
            onClick={() => setOpenMargin((v) => !v)}
          >
            <img className="navi_sidebar_icon" src="/images/ordersidebar_menu_icon3.svg" alt="Margin" width={20} height={20} />
            <div className={`dashboard_menu_hd ${marginSectionActive ? "active_ul" : ""}`}>Margin Orders</div>
            <span>
              <i className={openMargin ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true" />
            </span>
          </button>
          <div className={`collapse ${openMargin ? "show" : ""}`}>
            <ul className="btn-toggle-nav list-unstyled fw-normal pb-1 small">
              <li className={liActive("margin_orders")} onClick={fire("Margin Orders")}>
                <Link to="/user_profile/orders/margin_orders" className="rounded">Open Orders</Link>
              </li>
              <li className={liActive("margin_order_history")} onClick={fire("Margin Orders")}>
                <Link to="/user_profile/orders/margin_order_history" className="rounded">Order History</Link>
              </li>
              <li className={liActive("margin_position_history")} onClick={fire("Margin Orders")}>
                <Link to="/user_profile/orders/margin_position_history" className="rounded">Position History</Link>
              </li>
              <li className={liActive("margin_trade_history")} onClick={fire("Margin Orders")}>
                <Link to="/user_profile/orders/margin_trade_history" className="rounded">Trade History</Link>
              </li>
              <li className={liActive("margin_transfer_history")} onClick={fire("Margin Orders")}>
                <Link to="/user_profile/orders/margin_transfer_history" className="rounded">Transfer History</Link>
              </li>
              <li className={liActive("margin_borrowing_history")} onClick={fire("Margin Orders")}>
                <Link to="/user_profile/orders/margin_borrowing_history" className="rounded">Borrowing History</Link>
              </li>
            </ul>
          </div>
        </li>
      ) : (
        <li className={marginSectionActive ? "active mb-1" : "mb-1"} onClick={fire("Margin Orders")}>
          <Link to="/user_profile/orders/margin_orders">
            <img className="navi_sidebar_icon" src="/images/ordersidebar_menu_icon3.svg" alt="Margin" width={20} height={20} />
            <div className="dashboard_menu_hd">Margin Orders</div>
          </Link>
        </li>
      )}

      {isDesktop ? (
        <li className={`mb-1 order-sidebar-deep ${openOtc ? "active" : ""}`}>
          <button
            type="button"
            className={`btn btn-toggle collapsed ${otcSectionActive ? "active" : ""}`}
            onClick={() => setOpenOtc((v) => !v)}
          >
            <img className="navi_sidebar_icon" src="/images/ordersidebar_menu_icon4.svg" alt="OTC" width={20} height={20} />
            <div className={`dashboard_menu_hd ${otcSectionActive ? "active_ul" : ""}`}>OTC</div>
            <span>
              <i className={openOtc ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true" />
            </span>
          </button>
          <div className={`collapse ${openOtc ? "show" : ""}`}>
            <ul className="btn-toggle-nav list-unstyled fw-normal pb-1 small">
              <li className={liActive("otc_trade")} onClick={fire("OTC")}>
                <Link to="/user_profile/orders/otc_trade" className="rounded">Trade Desk</Link>
              </li>
              <li className={liActive("otc_orders")} onClick={fire("OTC")}>
                <Link to="/user_profile/orders/otc_orders" className="rounded">Active</Link>
              </li>
              <li className={liActive("otc_order_history")} onClick={fire("OTC")}>
                <Link to="/user_profile/orders/otc_order_history" className="rounded">History</Link>
              </li>
            </ul>
          </div>
        </li>
      ) : (
        <li className={otcSectionActive ? "active mb-1" : "mb-1"} onClick={fire("OTC")}>
          <Link to="/user_profile/orders/otc_trade">
            <img className="navi_sidebar_icon" src="/images/ordersidebar_menu_icon4.svg" alt="OTC" width={20} height={20} />
            <div className="dashboard_menu_hd">OTC</div>
          </Link>
        </li>
      )}

      {/* <li className={liActive("convert_orders")} onClick={fire("Convert")}>
        <Link to="/user_profile/orders/convert_orders">
          <i className="ri-exchange-line navi_sidebar_icon" aria-hidden="true" />
          <img className="navi_sidebar_icon" src="/images/ordersidebar_menu_icon4.svg" alt="Convert" width={20} height={20} />
          <div className="dashboard_menu_hd">Convert</div>
        </Link>
      </li>

      <li className={liActive("launchpad_orders")} onClick={fire("Launchpad")}>
        <Link to="/user_profile/orders/launchpad_orders">
          <img className="navi_sidebar_icon" src="/images/ordersidebar_menu_icon5.svg" alt="Launchpad" width={20} height={20} />
          <div className="dashboard_menu_hd">Launchpad</div>
        </Link>
      </li> */}

      {isDesktop ? (
        <li className={`mb-1 order-sidebar-deep ${openEarning ? "active" : ""}`}>
          <button
            type="button"
            className={`btn btn-toggle collapsed ${earningSectionActive ? "active" : ""}`}
            onClick={() => setOpenEarning((v) => !v)}
          >
            <img className="navi_sidebar_icon" src="/images/ordersidebar_menu_icon6.svg" alt="Earning" width={20} height={20} />
            <div className={`dashboard_menu_hd ${earningSectionActive ? "active_ul" : ""}`}>Earning</div>
            <span>
              <i className={openEarning ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true" />
            </span>
          </button>
          <div className={`collapse ${openEarning ? "show" : ""}`}>
            <ul className="btn-toggle-nav list-unstyled fw-normal pb-1 small">
              <li className={liActive("staking_orders")} onClick={fire("Earning")}>
                <Link to="/user_profile/orders/staking_orders" className="rounded">
                  Staking
                </Link>
              </li>
              <li className={liActive("launchpad_orders")} onClick={fire("Earning")}>
                <Link to="/user_profile/orders/launchpad_orders" className="rounded">
                  Launchpad
                </Link>
              </li>
            </ul>
          </div>
        </li>
      ) : (
        <li className={earningSectionActive ? "active mb-1" : "mb-1"} onClick={fire("Earning")}>
          <Link to="/user_profile/orders/staking_orders">
            <img className="navi_sidebar_icon" src="/images/ordersidebar_menu_icon6.svg" alt="Earning" width={20} height={20} />
            <div className="dashboard_menu_hd">Earning</div>
          </Link>
        </li>
      )}
      {/* 
      <li className={liActive("copy_trading_orders")} onClick={fire("Copy Trading")}>
        <Link to="/user_profile/orders/copy_trading_orders">
          <i className="ri-file-copy-line navi_sidebar_icon" aria-hidden="true" />
          <img className="navi_sidebar_icon" src="/images/ordersidebar_menu_icon7.svg" alt="Copy Trading" width={20} height={20} />
          <div className="dashboard_menu_hd">Copy Trading</div>
        </Link>
      </li> */}

      <li className={liActive("fiat_asset_order")} onClick={fire("Fiat Activity")}>
        <Link to="/user_profile/orders/fiat_asset_order">
          <img className="navi_sidebar_icon" src="/images/ordersidebar_menu_icon10.svg" alt="Fiat" width={20} height={20} />
          <div className="dashboard_menu_hd">Fiat Activity</div>
        </Link>
      </li>

      <li className={liActive("deposit_history")} onClick={fire("Deposit History")}>
        <Link to="/user_profile/orders/deposit_history">
          <img className="navi_sidebar_icon" src={ORDER_ICON_SRC.deposit} alt="Deposit" width={20} height={20} />
          <div className="dashboard_menu_hd">Deposit History</div>
        </Link>
      </li>

      <li className={liActive("withdraw_history")} onClick={fire("Withdraw History")}>
        <Link to="/user_profile/orders/withdraw_history">
          <img className="navi_sidebar_icon" src={ORDER_ICON_SRC.withdraw} alt="Withdraw" width={20} height={20} />
          <div className="dashboard_menu_hd">Withdraw History</div>
        </Link>
      </li>

      <li className={liActive("transfer")} onClick={fire("Transfer")}>
        <Link to="/user_profile/orders/transfer">
          <img className="navi_sidebar_icon" src={ORDER_ICON_SRC.transfer} alt="Transfer" width={20} height={20} />
          <div className="dashboard_menu_hd">Transfer</div>
        </Link>
      </li>

      {/* <li className={liActive("referral")} onClick={fire("Referral")}>
        <Link to="/user_profile/orders/referral">
          <img className="navi_sidebar_icon" src={ORDER_ICON_SRC.referral} alt="Referral" width={20} height={20} />
          <div className="dashboard_menu_hd">Referral</div>
        </Link>
      </li> */}

      {/* <li className={liActive("quick_buy_sell")} onClick={fire("Quick Buy/Sell")}>
        <Link to="/user_profile/orders/quick_buy_sell">
        <img className="navi_sidebar_icon" src="/images/ordersidebar_menu_icon12.svg" alt="History" width={20} height={20} />
          <div className="dashboard_menu_hd">Quick Buy/Sell</div>
        </Link>
      </li> */}

      {/* <li className={liActive("swap")} onClick={fire("Quick Swap")}>
        <Link to="swap">
          <i className="ri-flashlight-line navi_sidebar_icon" aria-hidden="true" />
          <div className="dashboard_menu_hd">Quick Buy/Sell</div>
        </Link>
      </li> */}

      {/* <li className={liActive("open_orders")} onClick={fire("Open Order")}>
        <Link to="open_orders">
          <i className="ri-file-list-3-line navi_sidebar_icon" aria-hidden="true" />
          <div className="dashboard_menu_hd">Open Orders</div>
        </Link>
      </li> */}
    </ul>
  );
}
